/* global document */
import { isChunkLike, stringFromBase64URL } from '@supabase/ssr'

type Cookie = { name: string; value: string }

const BASE64_PREFIX = 'base64-'
// sb-<ref>-auth-token and its chunks .0, .1, ... (not the -code-verifier cookie)
const AUTH_COOKIE = /^(sb-.+-auth-token)(?:\.(?:0|[1-9]\d*))?$/

// The value @supabase/ssr reads for a key: the cookie itself, else its chunks key.0, key.1, ... joined (combineChunks)
const combined = (cookies: Cookie[], key: string) => {
  const get = (name: string) => cookies.find((cookie) => cookie.name === name)?.value || null
  const whole = get(key)
  if (whole) return whole
  const chunks: string[] = []
  for (let i = 0; ; i++) {
    const chunk = get(`${key}.${i}`)
    if (!chunk) break
    chunks.push(chunk)
  }
  return chunks.length ? chunks.join('') : null
}

/**
 * Names of the Supabase auth cookies (all chunks) whose session @supabase/ssr 0.4 can't decode: a "base64-" value with a
 * character outside base64url, or bytes that aren't UTF-8 (e.g. a truncated chunk). Its decoder throws on those and the
 * error escapes supabase.auth.getUser(), so every page returned 500 until the user cleared their cookies. Checked with
 * the library's own decoder, so a cookie it can read is never dropped.
 */
export const corruptAuthCookieNames = (cookies: Cookie[]): string[] => {
  const keys = new Set(cookies.map((cookie) => cookie.name.match(AUTH_COOKIE)?.[1]).filter((key): key is string => !!key))
  const corrupt: string[] = []
  for (const key of keys) {
    const value = combined(cookies, key)
    if (!value?.startsWith(BASE64_PREFIX)) continue
    try {
      stringFromBase64URL(value.slice(BASE64_PREFIX.length))
    } catch {
      corrupt.push(...cookies.filter((cookie) => isChunkLike(cookie.name, key)).map((cookie) => cookie.name))
    }
  }
  return corrupt
}

/** In the browser: delete corrupt auth cookies before the Supabase client reads document.cookie */
export const dropCorruptAuthCookiesInBrowser = () => {
  if (typeof document === 'undefined') return
  const cookies = document.cookie
    .split(';')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const i = pair.indexOf('=')
      const name = i === -1 ? pair : pair.slice(0, i)
      const raw = i === -1 ? '' : pair.slice(i + 1)
      let value = raw
      try {
        value = decodeURIComponent(raw)
      } catch {
        // leave it as it is
      }
      return { name, value }
    })
  for (const name of corruptAuthCookieNames(cookies)) document.cookie = `${name}=; Max-Age=0; Path=/`
}
